import type { TmTask } from "../../api/tm";
import {
  useParties,
  useParty,
  usePartyLinks,
  useProject,
  useWorkItems,
} from "../../hooks/useDirectory";
import { formatDay, isoToDayKey, todayKey } from "../../lib/date";

/** Provenance d'une tâche : contexte PM (project/work item) ou CRM (party),
 * en lecture seule (DT décisions A2 & A6). Rien à afficher pour une tâche
 * native sans contexte. Pas de deep-link : les apps PM/CRM n'exposent pas
 * d'URL front ici.
 * ponytail: lien « Ouvrir dans PM/CRM » quand VITE_PM_APP_URL / VITE_CRM_APP_URL existeront. */
export function ProvenanceSection({ task }: { task: TmTask }) {
  const isPm = task.context_type?.startsWith("pm");
  const isCrm = task.context_type?.startsWith("crm");
  const projectId =
    task.context_type === "pm.project" ? (task.context_id ?? null) : null;
  const partyId = isCrm ? (task.context_id ?? null) : null;

  const project = useProject(projectId);
  const workItems = useWorkItems(projectId);
  const party = useParty(partyId);
  const links = usePartyLinks(partyId);
  // Annuaire parties déjà en cache (UserPicker) — résout les ids liés en noms.
  // ponytail: page unique de 100 parties, fallback id brut au-delà — passer à
  // des getParty ciblés si un tenant dépasse ce volume.
  const parties = useParties(undefined);

  if (!task.context_type) return null;

  const label = task.context_label || task.context_id;
  const partyName = (id: string) =>
    parties.data?.find((p) => p.id === id)?.display_name ?? id;

  const today = todayKey();
  const openItems = (workItems.data ?? []).filter((w) => !w.completed_at);
  const nextMilestone = openItems
    .filter(
      (w) =>
        w.type.toLowerCase().includes("milestone") &&
        w.due_date &&
        isoToDayKey(w.due_date) >= today,
    )
    .sort((a, b) => a.due_date!.localeCompare(b.due_date!))[0];

  return (
    <div className="detail-section">
      <div className="detail-section-title">
        Provenance{" "}
        <span
          className="chip"
          style={{ color: "var(--violet)", background: "var(--violet-soft)" }}
        >
          {isPm ? "Project Management" : isCrm ? "CRM" : task.context_type}
        </span>
      </div>

      <div className="prop-row">
        <span className="prop-label">Contexte</span>
        <span className="prop-value">
          {label}
          <span
            className="mono"
            style={{ fontSize: 10.5, color: "var(--ink-ghost)", marginLeft: 6 }}
          >
            {task.context_type}
          </span>
        </span>
      </div>

      {projectId && project.data && (
        <>
          <div className="prop-row">
            <span className="prop-label">Projet</span>
            <span className="prop-value">
              {project.data.name}{" "}
              <span className="tag mono">{project.data.code}</span>
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Avancement</span>
            <span
              className="prop-value"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <span className="progress-bar" style={{ width: 90 }}>
                <span
                  className="progress-fill"
                  style={{
                    width: `${Math.min(100, project.data.progress_pct)}%`,
                  }}
                />
              </span>
              {project.data.progress_pct}%
              <span className="tag">{project.data.status}</span>
            </span>
          </div>
          {workItems.data && (
            <div className="prop-row">
              <span className="prop-label">Work items</span>
              <span className="prop-value">
                {openItems.length} ouverts / {workItems.data.length}
              </span>
            </div>
          )}
          {nextMilestone && (
            <div className="prop-row">
              <span className="prop-label">Prochain jalon</span>
              <span className="prop-value">
                {nextMilestone.title}
                <span className="task-due" style={{ marginLeft: 6 }}>
                  ◷ {formatDay(isoToDayKey(nextMilestone.due_date!))}
                </span>
              </span>
            </div>
          )}
        </>
      )}

      {partyId && party.data && (
        <>
          <div className="prop-row">
            <span className="prop-label">Contact</span>
            <span className="prop-value">
              {party.data.display_name}{" "}
              <span className="tag">{party.data.party_type}</span>
            </span>
          </div>
          {links.data &&
            (links.data.roles.length > 0 ||
              links.data.relationships.length > 0) && (
              <div className="prop-row">
                <span className="prop-label">Personnes liées</span>
                <span
                  className="prop-value"
                  style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                >
                  {links.data.roles.map((r) => (
                    <span key={r.id} className="tag">
                      {partyName(
                        r.person_party_id === partyId
                          ? r.organization_party_id
                          : r.person_party_id,
                      )}{" "}
                      · {r.work_code}
                    </span>
                  ))}
                  {links.data.relationships.map((r) => (
                    <span key={r.id} className="tag">
                      {partyName(
                        r.party_a_id === partyId ? r.party_b_id : r.party_a_id,
                      )}{" "}
                      · {r.relationship_type_code}
                    </span>
                  ))}
                </span>
              </div>
            )}
        </>
      )}

      {(isPm || isCrm) &&
        (project.isError || party.isError || links.isError) && (
          <p style={{ fontSize: 11, color: "var(--ink-ghost)" }}>
            Détail {isPm ? "PM" : "CRM"} indisponible (module non déployé).
          </p>
        )}
    </div>
  );
}
